import React from 'react';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import CountUp from 'react-countup';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocale } from '../../context/LocaleContext';

type Point = { date: string; value: number };

interface CartValueKPIProps {
  total: number;
  deltaPct?: number;
  series: Point[];
  title?: string;
  dateRange?: string;
  onPrevious?: () => void;
  onNext?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onReturnToPresent?: () => void;
  isAtPresent?: boolean;
  displayMode?: 'currency' | 'percentage'; // Add display mode
  subtitle?: string; // Add custom subtitle
}

export function CartValueKPI({
  total,
  deltaPct,
  series,
  title = "Cart Value",
  dateRange,
  onPrevious,
  onNext,
  canGoPrevious = true,
  canGoNext = true,
  onReturnToPresent,
  isAtPresent = true,
  displayMode = 'currency',
  subtitle,
}: CartValueKPIProps) {
  const { formatDate, formatCurrency } = useLocale();
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const formattedDate = formatDate(data.date, { 
        day: 'numeric', 
        month: 'short' 
      });
      
      return (
        <div className="dark:bg-surface bg-white border dark:border-border-color border-gray-200 rounded-lg shadow-lg p-3">
          <p className="text-xs dark:text-text-secondary text-gray-600 mb-1">{formattedDate}</p>
          <p className="text-sm font-semibold dark:text-white text-gray-900">
            {displayMode === 'percentage' 
              ? `${data.value.toFixed(1)}%`
              : formatCurrency(data.value)
            }
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col items-center w-full h-full">
      <p className="text-m font-semibold dark:text-text-primary text-light-text-primary mb-3">{title}</p>
      
      <div className="rounded-2xl border dark:border-border-color border-light-border-color dark:bg-surface bg-light-surface p-4 space-y-3 w-full">
        <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary">
            {subtitle || (displayMode === 'percentage' ? 'Product mention rate (30d)' : 'Cart value (30d)')}
          </div>
          <div className="text-2xl font-semibold dark:text-white text-gray-900">
            {displayMode === 'percentage' ? (
              <CountUp
                end={total}
                duration={0.8}
                decimals={1}
                decimal="."
                suffix="%"
              />
            ) : (
              <CountUp
                end={total}
                duration={0.8}
                separator=","
                decimals={2}
                decimal="."
                prefix="€"
              />
            )}
          </div>
        </div>
        {typeof deltaPct === 'number' && (
          <span
            className={`text-xs rounded-full px-2 py-1 font-medium ${
              deltaPct >= 0
                ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300'
                : 'bg-rose-500/15 text-rose-600 dark:text-rose-300'
            }`}
          >
            {deltaPct >= 0 ? '↑' : '↓'} {Math.abs(deltaPct).toFixed(1)}%
          </span>
        )}
      </div>

      <div className="h-16">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series}>
            <defs>
              <linearGradient id="cartValueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366F1" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#6366F1" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#6366F1', strokeWidth: 1 }} />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#6366F1"
              strokeWidth={2}
              fill="url(#cartValueGradient)"
              dot={{ fill: '#6366F1', r: 3 }}
              activeDot={{ r: 5, fill: '#6366F1' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Date Navigation */}
      {dateRange && (
        <div className="flex items-center justify-center gap-4 pt-2">
          <button
            onClick={onPrevious}
            disabled={!canGoPrevious}
            className={`p-1 rounded transition-colors ${
              !canGoPrevious
                ? 'dark:text-text-secondary/30 text-light-text-secondary/30 cursor-not-allowed'
                : 'dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-text-primary hover:text-light-text-primary'
            }`}
            aria-label="Previous week"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          <div className="text-center min-w-[140px]">
            <p className="text-xs dark:text-text-secondary text-light-text-secondary">
              {dateRange}
            </p>
            {!isAtPresent && onReturnToPresent && (
              <button
                onClick={onReturnToPresent}
                className="text-[10px] text-primary hover:text-primary-hover underline mt-0.5"
              >
                Today
              </button>
            )}
          </div>
          
          <button
            onClick={onNext}
            disabled={!canGoNext}
            className={`p-1 rounded transition-colors ${
              !canGoNext
                ? 'dark:text-text-secondary/30 text-light-text-secondary/30 cursor-not-allowed'
                : 'dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-text-primary hover:text-light-text-primary'
            }`}
            aria-label="Next week"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
